import { API_URL } from './apiDictionary';
import { getClient } from './client';
import {
  ReaderTaskDicomStudy,
  ReaderTaskType,
} from '@shared/types/reader';
import { PaginatedResponse } from '@shared/types/general';
import { fetchReaderTasksMock } from '@api/mocks/readerTasks';
import { fetchReaderTaskMocks, fetchParentReaderTaskMocks } from './mocks/readerTask';
import {
  ReaderTasksFilters,
  Reading,
  ReaderAllTasksFilters,
} from '@shared/types/readings.types';
import { prepareUrl } from 'src/utils/routerHelpers';

export type ReaderTaskFetchProps = {
  clinicalTrialId: string;
  taskId: string;
  parentTaskId?: string | null;
  endpoint?: string;
};

export type ReaderParentTaskFetchProps = ReaderTaskFetchProps & {
  parentTaskId: string;
}

export type SaveReaderTaskProps = ReaderTaskFetchProps & {
  shouldRedirect?: boolean;
  parentTaskId?: string | null;
  body: {
    data: unknown;
  };
};

export type SaveParentReaderTaskProps = ReaderParentTaskFetchProps & SaveReaderTaskProps;

export type CompleteReaderTaskProps = ReaderTaskFetchProps & {
  shouldRedirect?: boolean;
  data: {
    userName: string;
    password: string;
    signAgreement: boolean;
    signMessage: string;
    formData: unknown;
  };
};

export type CompleteParentReaderTaskProps = ReaderParentTaskFetchProps & CompleteReaderTaskProps;

export const fetchReaderTasks = (
  clinicalTrialId: string,
  filters: Partial<ReaderTasksFilters>,
  page: number,
  size: number,
  endpoint?: string
) => {
  const _filters = {
    ...filters,
    statuses: filters.statuses?.join(','),
  };
  fetchReaderTasksMock({ ..._filters, page, size });
  const finalEndpoint = endpoint || API_URL.readerTasks;
  const api = getClient(finalEndpoint);

  return api.get<PaginatedResponse<Reading>>(finalEndpoint, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
    params: {
      page: page ? page - 1 : 0,
      size: size ?? 10,
      ..._filters,
    },
    id: 'reader-tasks',
  });
};

export const fetchReaderTasksAll = (
  clinicalTrialId: string,
  filters: Partial<ReaderAllTasksFilters>,
  endpoint?: string
) => {
  const _filters = {
    ...filters,
  };

  const finalEndpoint = endpoint || API_URL.readerTasksAll;
  const api = getClient(finalEndpoint);

  return api.get<Reading[]>(finalEndpoint, {
    headers: {
      'x-dynamika-clinical-trial-id': clinicalTrialId,
    },
    params: {
      ..._filters,
    },
    id: 'reader-tasks-all',
  });
};

export const fetchReaderTask = (args: ReaderTaskFetchProps) => {
  fetchReaderTaskMocks(args);

  const finalEndpoint = args.endpoint || API_URL.readerTask;

  const api = getClient(
    finalEndpoint,
    new URLSearchParams(location.search).has('genantOpt')
  );

  return api.get<ReaderTaskType>(prepareUrl(finalEndpoint, [args.taskId]), {
    headers: {
      'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
    },
  });
};

/**
 * Every study of a grouped reading, asked for by the parent
 *
 * Plain terms
 * -----------
 * A reading that covers several visits keeps its images spread across those
 * visits. This asks once, at the level of the whole reading, so the viewer can
 * show earlier timepoints beside the one being scored.
 *
 * Technical
 * ---------
 * `taskId` is the PARENT task id and fills the endpoint's single `:taskId`
 * segment. The backend's two-segment variant answers for one child task and is
 * deliberately not reachable from here (D7-7712, A1). Returns the raw axios
 * response like its neighbours; the empty-body fallback lives in the worker.
 */
export const fetchReadingDicomSeries = (args: ReaderTaskFetchProps) => {
  const finalEndpoint = args.endpoint || API_URL.readerReadingDicomSeries;

  const api = getClient(finalEndpoint);

  return api.get<ReaderTaskDicomStudy[]>(
    prepareUrl(finalEndpoint, [args.taskId]),
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const fetchParentReaderTask = (args: ReaderParentTaskFetchProps) => {
  fetchParentReaderTaskMocks(args);

  const finalEndpoint = args.endpoint || API_URL.readerParentTask;

  const api = getClient(
    finalEndpoint,
    new URLSearchParams(location.search).has('genantOpt')
  );

  return api.get<ReaderTaskType>(prepareUrl(finalEndpoint, [args.parentTaskId, args.taskId]), {
    headers: {
      'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
    },
  });
};

export const fetchReaderDICOMResource = async (
  clinicalTrialId: string,
  endpoint?: string
) => {
  const finalEndpoint = endpoint || API_URL.readerDICOMResource;

  const api = getClient(finalEndpoint);
  try {
    const response = await api.get<{ url: string }>(finalEndpoint, {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching DICOM Resource', error);
    throw error;
  }
};

export const startReaderTask = (args: ReaderTaskFetchProps) => {
  const api = getClient(API_URL.readerTaskStart);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerTaskStart, [args.taskId]),
    undefined,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const startParentReaderTask = (args: ReaderParentTaskFetchProps) => {
  const api = getClient(API_URL.readerParentTaskStart);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerParentTaskStart, [args.parentTaskId, args.taskId]),
    undefined,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const saveReaderTask = (args: SaveReaderTaskProps) => {
  const api = getClient(API_URL.readerTaskScoring);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerTaskScoring, [args.taskId]),
    args.body,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const saveParentReaderTask = (args: SaveParentReaderTaskProps) => {
  const api = getClient(API_URL.readerParentTaskScoring);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerParentTaskScoring, [args.parentTaskId, args.taskId]),
    args.body,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const completeReaderTask = (args: CompleteReaderTaskProps) => {
  const api = getClient(API_URL.readerTaskComplete);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerTaskComplete, [args.taskId]),
    args.data,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};

export const completeParentReaderTask = (args: CompleteParentReaderTaskProps) => {
  const api = getClient(API_URL.readerParentTaskComplete);

  return api.put<ReaderTaskType>(
    prepareUrl(API_URL.readerParentTaskComplete, [args.parentTaskId, args.taskId]),
    args.data,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args?.clinicalTrialId,
      },
    }
  );
};
